const DEFAULT_STATUS_QUERY = 'integration_status';
const DEFAULT_CONNECTION_ID_QUERY = 'integration_connection_id';
const DEFAULT_ERROR_QUERY = 'integration_error';
export const BUSTLY_HOSTED_CONNECT_HANDOFF_STORAGE_KEY = 'bustly:hosted-connect-handoff';
export const BUSTLY_HOSTED_CONNECT_RESULT_MESSAGE = 'bustly:hosted-connect-result';
export const BUSTLY_HOSTED_CONNECT_RESULT_CHANNEL = 'bustly:hosted-connect-result';
export const BUSTLY_HOSTED_CONNECT_FLOW_QUERY_PARAMETER = 'integration_flow_id';
const POPUP_CLOSE_GRACE_MS = 1500;

export const bustlyHostedConnectFlowIdFromCallbackUrl = (href: string): string | undefined => {
    const flowId = new URL(href).searchParams
        .get(BUSTLY_HOSTED_CONNECT_FLOW_QUERY_PARAMETER)
        ?.trim();
    return flowId || undefined;
};

export const bustlyHostedConnectResultChannelName = (
    flowId?: string | null,
): string => (
    flowId ? `${BUSTLY_HOSTED_CONNECT_RESULT_CHANNEL}:${flowId}` : BUSTLY_HOSTED_CONNECT_RESULT_CHANNEL
);

export interface BustlyHostedConnectHandoff {
    connectionIdQueryParameter?: string;
    statusQueryParameter?: string;
    errorQueryParameter?: string;
}

const normalizeHandoff = (value: unknown): BustlyHostedConnectHandoff | undefined => {
    if (!value || typeof value !== 'object') {
        return undefined;
    }

    const candidate = value as Partial<BustlyHostedConnectHandoff>;
    const handoff: BustlyHostedConnectHandoff = {};

    if (typeof candidate.connectionIdQueryParameter === 'string' && candidate.connectionIdQueryParameter.trim()) {
        handoff.connectionIdQueryParameter = candidate.connectionIdQueryParameter;
    }
    if (typeof candidate.statusQueryParameter === 'string' && candidate.statusQueryParameter.trim()) {
        handoff.statusQueryParameter = candidate.statusQueryParameter;
    }
    if (typeof candidate.errorQueryParameter === 'string' && candidate.errorQueryParameter.trim()) {
        handoff.errorQueryParameter = candidate.errorQueryParameter;
    }

    return Object.keys(handoff).length > 0 ? handoff : undefined;
};

export const persistBustlyHostedConnectHandoff = (
    popup: Window,
    handoff?: BustlyHostedConnectHandoff | null
) => {
    try {
        const normalized = normalizeHandoff(handoff);
        if (!normalized) {
            popup.sessionStorage.removeItem(BUSTLY_HOSTED_CONNECT_HANDOFF_STORAGE_KEY);
            return;
        }
        popup.sessionStorage.setItem(
            BUSTLY_HOSTED_CONNECT_HANDOFF_STORAGE_KEY,
            JSON.stringify(normalized)
        );
    } catch {
        // Popup storage is a best-effort optimization for callback parsing.
    }
};

export const readBustlyHostedConnectHandoff = (): BustlyHostedConnectHandoff | undefined => {
    try {
        const raw = window.sessionStorage.getItem(BUSTLY_HOSTED_CONNECT_HANDOFF_STORAGE_KEY);
        window.sessionStorage.removeItem(BUSTLY_HOSTED_CONNECT_HANDOFF_STORAGE_KEY);
        if (!raw) {
            return undefined;
        }
        return normalizeHandoff(JSON.parse(raw));
    } catch {
        try {
            window.sessionStorage.removeItem(BUSTLY_HOSTED_CONNECT_HANDOFF_STORAGE_KEY);
        } catch {
            // Ignore storage cleanup failures.
        }
        return undefined;
    }
};

export type BustlyHostedConnectResult = {
    status: 'success' | 'pending' | 'error';
    connectionId?: string;
    error?: string;
};

export type BustlyHostedConnectResultMessage = {
    type: typeof BUSTLY_HOSTED_CONNECT_RESULT_MESSAGE;
    result: BustlyHostedConnectResult;
    flowId?: string;
};

export const isBustlyHostedConnectResultMessage = (
    value: unknown,
): value is BustlyHostedConnectResultMessage => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const message = value as Partial<BustlyHostedConnectResultMessage>;
    const result = message.result;
    return message.type === BUSTLY_HOSTED_CONNECT_RESULT_MESSAGE
        && !!result
        && ['success', 'pending', 'error'].includes(String(result.status))
        && (message.flowId == null || typeof message.flowId === 'string')
        && (result.connectionId == null || typeof result.connectionId === 'string')
        && (result.error == null || typeof result.error === 'string');
};

export const parseBustlyHostedConnectResult = (
    href: string,
    handoff?: BustlyHostedConnectHandoff
): BustlyHostedConnectResult | null => {
    const url = new URL(href);
    const status = String(
        url.searchParams.get(handoff?.statusQueryParameter || DEFAULT_STATUS_QUERY) || ''
    ).toLowerCase();
    if (!status) {
        return null;
    }

    const connectionId = url.searchParams.get(
        handoff?.connectionIdQueryParameter || DEFAULT_CONNECTION_ID_QUERY
    ) || undefined;
    const error = url.searchParams.get(
        handoff?.errorQueryParameter || DEFAULT_ERROR_QUERY
    ) || undefined;

    if (['success', 'active', 'connected'].includes(status)) {
        return connectionId
            ? { status: 'success', connectionId }
            : { status: 'error', error: 'Hosted Connect did not return a connection ID' };
    }
    if (status === 'pending') {
        return { status: 'pending', connectionId };
    }
    return { status: 'error', connectionId, error: error || 'Hosted Connect failed' };
};

export const waitForBustlyHostedConnectPopupResult = (
    popup: Window,
    expectedOrigin: string,
    handoff?: BustlyHostedConnectHandoff,
    expectedFlowId?: string,
    timeoutMs = 5 * 60 * 1000,
    popupCloseGraceMs = POPUP_CLOSE_GRACE_MS,
): Promise<BustlyHostedConnectResult> => new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let settled = false;
    let interval: number | undefined;
    let onMessage: (event: MessageEvent<unknown>) => void;
    let resultChannel: BroadcastChannel | undefined;
    let popupClosedAt: number | undefined;
    const finish = (callback: () => void) => {
        if (settled) return;
        settled = true;
        if (interval != null) {
            window.clearInterval(interval);
        }
        window.removeEventListener('message', onMessage);
        resultChannel?.close();
        callback();
    };
    const matchesExpectedFlow = (message: BustlyHostedConnectResultMessage): boolean => (
        !expectedFlowId || message.flowId === expectedFlowId
    );
    onMessage = (event: MessageEvent<unknown>) => {
        if (event.origin !== expectedOrigin || event.source !== popup) {
            return;
        }
        if (isBustlyHostedConnectResultMessage(event.data) && matchesExpectedFlow(event.data)) {
            finish(() => resolve(event.data.result));
        }
    };
    window.addEventListener('message', onMessage);
    try {
        resultChannel = new BroadcastChannel(bustlyHostedConnectResultChannelName(expectedFlowId));
        resultChannel.onmessage = (event: MessageEvent<unknown>) => {
            if (isBustlyHostedConnectResultMessage(event.data) && matchesExpectedFlow(event.data)) {
                finish(() => resolve(event.data.result));
            }
        };
    } catch {
        // URL polling remains available in browsers without BroadcastChannel.
    }
    interval = window.setInterval(() => {
        if (Date.now() - startedAt >= timeoutMs) {
            finish(() => reject(new Error('Hosted Connect timed out')));
            return;
        }

        try {
            if (popup.closed) {
                // Cross-origin OAuth navigation can detach the original WindowProxy
                // and report it as closed while the visible popup remains focused.
                // Only treat a sustained closed signal after focus returns to the
                // parent as an intentional cancellation.
                if (typeof document !== 'undefined' && !document.hasFocus()) {
                    popupClosedAt = undefined;
                    return;
                }
                popupClosedAt ??= Date.now();
                if (Date.now() - popupClosedAt >= popupCloseGraceMs) {
                    finish(() => reject(new Error('Hosted Connect was cancelled')));
                }
                return;
            }
            popupClosedAt = undefined;
            const href = popup.location.href;
            if (!href || new URL(href).origin !== expectedOrigin) {
                return;
            }
            const result = parseBustlyHostedConnectResult(href, handoff);
            if (result) {
                finish(() => resolve(result));
            }
        } catch {
            // Cross-origin access is expected while the popup is at the Provider.
        }
    }, 250);
});
